'use client';

/**
 * ./src/modules/product/components/samples/SampleDepartmentList.tsx
 *
 * SampleDepartmentList component is used to display the list of sample departments.
 *
 * @copyright DesignShop, 2024
 * @since 07/15/2024
 */
import React, { useEffect, useState } from 'react';

import { QueryClient } from '@tanstack/react-query';

import { Carousel, CarouselContent, CarouselItem } from '@/modules/common/components/carousel';
import { BREAKPOINT_SM, BREAKPOINT_TABLET_LANDSCAPE } from '@/modules/common/constants';
import useIsMobile from '@/modules/common/hooks/useIsMobile';
import {
  ShopByDepartmentItemFragment,
  useShopByDepartmentCollectionQuery,
} from '@/modules/product/gql/__generated/shopByDepartmentCollection.generated';

import { SamplesDepartmentCard } from './SamplesDepartmentCard';

const innerWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
const queryClient = new QueryClient();

type SampleDepartmentListProps = {
  forceMobile?: boolean;
  departments?: ShopByDepartmentItemFragment[];
};

const SampleDepartmentList = ({
  forceMobile,
  departments: initialDepartments,
}: SampleDepartmentListProps) => {
  const [departments, setDepartments] = useState<ShopByDepartmentItemFragment[] | null>(
    initialDepartments ?? null
  );
  const isMobileHook = useIsMobile(BREAKPOINT_SM);
  const isMobile = forceMobile !== undefined ? forceMobile : isMobileHook;

  useEffect(() => {
    if (departments) {
      return;
    }

    const fetchDepartments = async () => {
      const shopByDepartmentData = await queryClient.fetchQuery({
        queryKey: useShopByDepartmentCollectionQuery.getKey(),
        queryFn: useShopByDepartmentCollectionQuery.fetcher(),
      });

      const departmentsRes = shopByDepartmentData?.shopByDepartmentCollection
        ?.items as ShopByDepartmentItemFragment[];

      if (departmentsRes) {
        setDepartments(departmentsRes);
      }
    };

    fetchDepartments();
  }, [departments]);

  if (!departments || !departments.length) {
    return null;
  }

  return (
    <div>
      {isMobile ?
        <div>
          <Carousel
            opts={{
              loop: true,
              dragFree: true,
              align: 'start',
            }}
            className="carousel-container-bleed overflow-hidden"
            data-testid="carousel"
          >
            <CarouselContent className="-ml-2.5" wrapperClassName="max-lg:overflow-visible">
              {departments?.map((department, index) => (
                <CarouselItem
                  key={department.slug}
                  className="aspect-square h-56 w-56 basis-auto pl-2.5"
                  data-testid={`carousel-item-${index}`}
                >
                  <SamplesDepartmentCard
                    {...department}
                    imageClassName="-top-2"
                    data-testid={`department-card-${index}`}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      : <div className="grid grid-cols-departmentsGrid gap-4 px-8">
          {departments?.map((department, index) => (
            <div
              key={department.slug}
              className="w-full"
              style={{
                aspectRatio:
                  innerWidth >= BREAKPOINT_SM && innerWidth <= BREAKPOINT_TABLET_LANDSCAPE ?
                    '1.18 / 1'
                  : '1',
              }}
              data-testid={`department-grid-item-${index}`}
            >
              <SamplesDepartmentCard {...department} data-testid={`department-card-${index}`} />
            </div>
          ))}
        </div>
      }
    </div>
  );
};

export default SampleDepartmentList;
